import React, { useCallback, useEffect, useRef, useState } from "react";
import Layout from "../../components/layout";
import { FaArrowLeft, FaSearch, FaPlus, FaTimes } from "react-icons/fa";
import { FaFileInvoice } from "react-icons/fa";
import { MdCheckBoxOutlineBlank, MdQrCodeScanner } from "react-icons/md";
import { ImPrinter } from "react-icons/im";

import styles from "./OpenOrderListPage.module.scss";
import { Link, useNavigate } from "react-router-dom";
import { route } from "../../static/route";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  fetchOpenOrdersData,
  fetchPartiesData,
  getOrders,
  getParties,
  getSelectedOrder,
  getSelectedParty,
  getTotalOrders,
  updateSelectedOrder,
  updateSelectedParty,
} from "../../features/openOrder/openOrderSlice";
import ListCard from "../../components/orders/ListCard";
import { OptionsProps, OrderProps } from "../../types/order";
import Select from "react-select";
import { selectStyles } from "../../static/styles";
import { FaSquareCheck } from "react-icons/fa6";
import OrderPdf from "../../components/orderDetails/OrderPdf";
import { useReactToPrint } from "react-to-print";
import { Html5Qrcode } from "html5-qrcode";
import { GET } from "../../services/axios.service";

const printStyles = `
  @media print {
    @page {
      size: A6 portrait;
      margin: 0;
    }
  }
`;

const OpenOrderListPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  let debounceTimeout: NodeJS.Timeout;
  const printRef = useRef<HTMLDivElement>(null);
  const orders = useAppSelector(getOrders);
  const totalOrders = useAppSelector(getTotalOrders);
  const parties = useAppSelector(getParties);
  const selectedParty = useAppSelector(getSelectedParty);
  const selectedOrder = useAppSelector(getSelectedOrder);

  const [page, setPage] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [firstLoading, setFirstLoading] = useState<boolean>(false);
  const [partiesOption, setPartiesOption] = useState<OptionsProps[]>([]);
  const [printOrders, setPrintOrders] = useState<OrderProps[]>([]);
  const [invoiceOrders, setInvoiceOrders] = useState<OrderProps[]>([]);
  const [showFilter, setShowFilter] = useState(false);
  const [addOrderId, setAddOrderId] = useState<string>("");
  const [addOrderError, setAddOrderError] = useState<string | null>(null);
  const [addOrderLoading, setAddOrderLoading] = useState<boolean>(false);
  const [showQrScanner, setShowQrScanner] = useState<boolean>(false);
  const [showAddInvoice, setShowAddInvoice] = useState<boolean>(false);
  const qrScannerRef = useRef<Html5Qrcode | null>(null);

  const handleGoToBack = () => {
    navigate(route.dashboard);
  };

  const handleChangeParty = (id: number) => {
    dispatch(updateSelectedParty(id));
    setPage(1);
  };

  const handleChangeOrderId = (id: number | null) => {
    dispatch(updateSelectedOrder(id));
    setPage(1);
  };

  const handleChangePrintOrder = (order: OrderProps, action: boolean) => {
    setPrintOrders((prevOrders) => {
      if (action) {
        if (
          !prevOrders.some((existingOrder) => existingOrder.id === order.id)
        ) {
          return [...prevOrders, order];
        }
        return prevOrders;
      } else {
        return prevOrders.filter(
          (existingOrder) => existingOrder.id !== order.id
        );
      }
    });
  };

  const handleChangeInvoiceOrder = (order: OrderProps, action: boolean) => {
    if (!selectedParty) {
      alert("Please select a party first");
      return;
    }
    setInvoiceOrders((prevOrders) => {
      if (action) {
        if (
          !prevOrders.some((existingOrder) => existingOrder.id === order.id)
        ) {
          return [...prevOrders, order];
        }
        return prevOrders;
      } else {
        return prevOrders.filter(
          (existingOrder) => existingOrder.id !== order.id
        );
      }
    });
  };

  const handleGoToDeliverOrders = () => {
    navigate(route.deliverOrders, {
      state: {
        orders: invoiceOrders,
        partyId: selectedParty,
      },
    });
  };

  const handlePrintOrderData = useReactToPrint({
    content: () => printRef.current,
    documentTitle: "Open Order PDF",
  });

  const handleAddOrderById = useCallback(
    async (orderId?: string) => {
      const id = orderId || addOrderId;
      if (!id) return;

      if (!selectedParty && invoiceOrders.length > 1) {
        setAddOrderError("Please select a party first");
        return;
      }

      setAddOrderError(null);
      setAddOrderLoading(true);

      try {
        const res: any = await GET(`/v1/worker/orders/${id}`, {});
        if (!res?.data?.data) {
          setAddOrderError("Order not found");
          setAddOrderLoading(false);
          return;
        }

        const order: OrderProps = res.data.data;

        if (order.status !== 0) {
          setAddOrderError("Order is already closed");
          setAddOrderLoading(false);
          return;
        }

        if (invoiceOrders.length === 0) {
          dispatch(updateSelectedParty(order.party_id));
        }

        if (invoiceOrders.length > 0 && order.party_id !== selectedParty) {
          setAddOrderError("Order belongs to a different party");
          setAddOrderLoading(false);
          return;
        }

        if (invoiceOrders.some((o) => o.id === order.id)) {
          setAddOrderError("Order already added");
          setAddOrderLoading(false);
          return;
        }
        
        setInvoiceOrders((prev) => [...prev, order]);
        setAddOrderId("");
        setAddOrderError(null);
      } catch {
        setAddOrderError("Order not found");
      } finally {
        setAddOrderLoading(false);
      }
    },
    [addOrderId, selectedParty, invoiceOrders]
  );

  const stopQrScanner = useCallback(() => {
    if (qrScannerRef.current) {
      qrScannerRef.current
        .stop()
        .then(() => {
          qrScannerRef.current?.clear();
          qrScannerRef.current = null;
        })
        .catch(() => {
          qrScannerRef.current = null;
        });
    }
    setShowQrScanner(false);
  }, []);

  const startQrScanner = useCallback(() => {
    setShowQrScanner(true);
    setAddOrderError(null);

    setTimeout(() => {
      const scanner = new Html5Qrcode("qr-reader");
      qrScannerRef.current = scanner;

      scanner
        .start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          (decodedText) => {
            try {
              const qrData = JSON.parse(decodedText);
              const { order_id, party_id } = qrData;

              if (!order_id) {
                setAddOrderError("Invalid QR code: missing order ID");
                return;
              }

              if (selectedParty && party_id !== selectedParty) {
                setAddOrderError("Order belongs to a different party");
                scanner.stop().then(() => {
                  scanner.clear();
                  qrScannerRef.current = null;
                  setShowQrScanner(false);
                });
                return;
              }

              if (!selectedParty && party_id) {
                dispatch(updateSelectedParty(party_id));
              }

              scanner.stop().then(() => {
                scanner.clear();
                qrScannerRef.current = null;
                setShowQrScanner(false);
                handleAddOrderById(String(order_id));
              });
            } catch {
              setAddOrderError("Invalid QR code format");
            }
          },
          () => {}
        )
        .catch(() => {
          setAddOrderError("Could not start camera. Please allow camera access.");
          setShowQrScanner(false);
        });
    }, 100);
  }, [selectedParty, dispatch, handleAddOrderById]);

  const handleRemoveInvoiceOrder = (orderId: number) => {
    setInvoiceOrders((prev) => prev.filter((o) => o.id !== orderId));
  };

  useEffect(() => {
    if (page === 1) setFirstLoading(true);
    setIsLoading(true);

    dispatch(
      fetchOpenOrdersData({
        page: page,
        party_id: selectedParty ? selectedParty : undefined,
        order_id: selectedOrder ? selectedOrder : undefined,
      })
    ).then(() => {
      setIsLoading(false);
      setFirstLoading(false);
    });
  }, [page, selectedParty, selectedOrder]);

  useEffect(() => {
    dispatch(fetchPartiesData());
  }, []);

  useEffect(() => {
    if (parties) {
      const options: OptionsProps[] = [];
      parties?.map((party) => {
        options.push({ value: party.id, label: party.firm_name });
      });
      setPartiesOption(options);
    }
  }, [parties]);

  useEffect(() => {
    return () => {
      if (qrScannerRef.current) {
        qrScannerRef.current.stop().catch(() => {});
      }
    };
  }, []);

  return (
    <Layout>
      <div className={styles.OpenOrderListPage}>
        <div className="w-full px-10 h-[200px] text-center flex flex-col justify-center relative">
          <FaArrowLeft
            onClick={handleGoToBack}
            className="absolute top-5 left-5 text-3xl text-white border-2 border-white p-1 rounded-full"
          />

          <h1 className="text-5xl mt-6 font-bold text-white">Open Orders</h1>

          {printOrders.length > 0 && (
            <div className="absolute top-5 right-5">
              <ImPrinter
                onClick={handlePrintOrderData}
                className="text-3xl text-white cursor-pointer"
              />
            </div>
          )}
        </div>

        <div className="w-full relative mt-[70px] px-10 py-5">
          <div className="flex gap-2 mb-5 flex-wrap justify-end">
            <button
              className="p-2 w-fit rounded-md flex items-center bg-gradient-to-r from-primary-1 to-primary-2 text-white transition-colors duration-300"
              onClick={() => {
                setShowFilter(!showFilter);
                if (!showFilter) { setShowAddInvoice(false); }
              }}
            >
              <FaSearch className="mr-2" />{" "}
              {showFilter ? "Hide Filter" : "Show Filter"}
            </button>
            <button
              className="p-2 w-fit rounded-md flex items-center bg-gradient-to-r from-primary-1 to-primary-2 text-white transition-colors duration-300"
              onClick={() => {
                setShowAddInvoice(!showAddInvoice);
                if (!showAddInvoice) { setShowFilter(false); }
              }}
            >
              <FaFileInvoice className="mr-2" />{" "}
              {showAddInvoice ? "Hide Invoice" : "Add to Invoice"}
            </button>
          </div>

          {showFilter && (
            <>
              <div className="bg-gradient-to-r mb-5 from-primary-1 to-primary-2 h-16 w-full relative rounded">
                <Select
                  isClearable
                  value={partiesOption.find(
                    (val) => val.value === selectedParty
                  )}
                  className="text-primary-1 p-0 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                  styles={selectStyles}
                  placeholder={"Enter party name"}
                  options={partiesOption}
                  onChange={(val: any) => handleChangeParty(val?.value)}
                />
              </div>
              <div className="bg-gradient-to-r mb-10 from-primary-1 to-primary-2 h-16 w-full relative rounded">
                <input
                  defaultValue={selectedOrder || ""}
                  className="text-primary-1 px-3 py-1 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                  placeholder="Enter order ID"
                  pattern="[0-9]*"
                  type="number"
                  onKeyUp={(e: any) => {
                    const value = e.target.value;

                    clearTimeout(debounceTimeout);
                    debounceTimeout = setTimeout(() => {
                      handleChangeOrderId(value || null);
                    }, 2000);
                  }}
                  onFocus={(e) =>
                    e.target.addEventListener(
                      "wheel",
                      function (e) {
                        e.preventDefault();
                      },
                      { passive: false }
                    )
                  }
                />
              </div>
            </>
          )}

          {/* Add Order to Invoice Section */}
          {showAddInvoice && (
            <div className="mb-5 p-4 bg-white rounded-lg shadow">
              <h3 className="text-lg font-bold text-primary-1 mb-3">
                Add Order to Invoice
              </h3>

              <div className="bg-gradient-to-r mb-3 from-primary-1 to-primary-2 h-12 w-full relative rounded">
                <Select
                  isClearable
                  value={partiesOption.find(
                    (val) => val.value === selectedParty
                  )}
                  className="text-primary-1 p-0 font-semibold text-lg placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                  styles={selectStyles}
                  placeholder={"Select party"}
                  options={partiesOption}
                  onChange={(val: any) => handleChangeParty(val?.value)}
                />
              </div>

              <div className="flex gap-2 mb-3">
                <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-12 flex-1 relative rounded">
                  <input
                    value={addOrderId}
                    onChange={(e) => {
                      setAddOrderId(e.target.value);
                      setAddOrderError(null);
                    }}
                    className="text-primary-1 px-3 py-1 font-semibold text-lg placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                    placeholder="Enter Order ID"
                    pattern="[0-9]*"
                    type="number"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleAddOrderById();
                    }}
                    onFocus={(e) =>
                      e.target.addEventListener(
                        "wheel",
                        function (e) {
                          e.preventDefault();
                        },
                        { passive: false }
                      )
                    }
                  />
                </div>

                <button
                  onClick={() => handleAddOrderById()}
                  disabled={addOrderLoading || !addOrderId}
                  className="px-4 h-12 rounded bg-gradient-to-r from-primary-1 to-primary-2 text-white font-bold flex items-center gap-1 disabled:opacity-50"
                >
                  <FaPlus /> {addOrderLoading ? "..." : "Add"}
                </button>
              </div>
              
              <div className="flex gap-2 mb-3 justify-center">
                <button
                  onClick={showQrScanner ? stopQrScanner : startQrScanner}
                  className="px-4 h-12 rounded bg-gradient-to-r from-primary-1 to-primary-2 text-white font-bold flex items-center gap-1"
                >
                  {showQrScanner ? (
                    <FaTimes className="text-lg" />
                  ) : (
                    <MdQrCodeScanner className="text-xl" />
                  )}
                  {showQrScanner ? "Close" : "Scan QR"}
                </button>
              </div>

              {addOrderError && (
                <p className="text-red-500 font-semibold text-sm mb-3">
                  {addOrderError}
                </p>
              )}

              {showQrScanner && (
                <div className="mb-3">
                  <div
                    id="qr-reader"
                    className="w-full max-w-[400px] mx-auto"
                  ></div>
                </div>
              )}

              {invoiceOrders.length > 0 && (
                <div className="mt-3 border-t pt-3">
                  <h4 className="text-sm font-bold text-primary-2 mb-2">
                    Invoice Orders ({invoiceOrders.length})
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {invoiceOrders.map((order) => (
                      <div
                        key={order.id}
                        className="flex items-center gap-1 bg-gradient-to-r from-primary-1 to-primary-2 text-white px-3 py-1 rounded-full text-sm font-semibold"
                      >
                        <span>#{order.id}</span>
                        <FaTimes
                          onClick={() => handleRemoveInvoiceOrder(order.id)}
                          className="cursor-pointer ml-1 text-xs"
                        />
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={handleGoToDeliverOrders}
                    className="mt-3 w-full py-2 rounded bg-gradient-to-r from-primary-1 to-primary-2 text-white font-bold flex items-center justify-center gap-2"
                  >
                    <FaFileInvoice /> Generate Invoice
                  </button>
                </div>
              )}
            </div>
          )}

          {firstLoading ? (
            <p className="mt-4 text-primary-2 font-bold text-2xl text-center">
              Please wait...
            </p>
          ) : (
            <>
              {orders && orders.length > 0 ? (
                <div>
                  {orders.map((order, index) => (
                      <div className="relative w-full h-full" key={index}>
                        <Link
                          to={route.orderDetails + `/${order.id}`}
                        >
                          <ListCard order={order} />
                        </Link>
  
                        <div className="absolute top-3 right-3 flex gap-3">
                          {printOrders.some((o) => o.id === order.id) ? (
                            <div>
                              <ImPrinter className="text-xl text-primary-1" />
                              <FaSquareCheck
                                onClick={(e: any) => {
                                  e.preventDefault();
                                  handleChangePrintOrder(order, false);
                                }}
                                className="text-xl text-primary-1"
                              />
                            </div>
                          ) : (
                            <div>
                              <ImPrinter className="text-xl text-primary-1" />
                              <MdCheckBoxOutlineBlank
                                onClick={(e: any) => {
                                  e.preventDefault();
                                  handleChangePrintOrder(order, true);
                                }}
                                className="text-xl text-primary-1"
                              />
                            </div>
                          )}
  
                          {invoiceOrders.some((o) => o.id === order.id) ? (
                            <div>
                              <FaFileInvoice className="text-xl text-primary-1" />
                              <FaSquareCheck
                                onClick={(e: any) => {
                                  e.preventDefault();
                                  handleChangeInvoiceOrder(order, false);
                                }}
                                className="text-xl text-primary-1"
                              />
                            </div>
                          ) : (
                            <div>
                              <FaFileInvoice className="text-xl text-primary-1" />
                              <MdCheckBoxOutlineBlank
                                onClick={(e: any) => {
                                  e.preventDefault();
                                  handleChangeInvoiceOrder(order, true);
                                }}
                                className="text-xl text-primary-1"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="text-center">
                  <p className="mt-4 text-primary-2 font-bold text-2xl">
                    No order details found.
                  </p>

                  <div className="w-full flex justify-center">
                    <Link to={route.newOrder}>
                      <button
                        type="button"
                        className="mx-auto mt-8 shadow rounded-3xl text-3xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-10 py-2"
                      >
                        Add Order
                      </button>
                    </Link>
                  </div>
                </div>
              )}

              {totalOrders > 0 && totalOrders > orders.length && (
                <div className="flex">
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={isLoading}
                    type="button"
                    className="mx-auto mt-8 shadow rounded-3xl text-3xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-10 py-2"
                  >
                    {isLoading ? "Please wait..." : "View More"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div ref={printRef} className="print:grid hidden">
        <style>{printStyles}</style>

        {printOrders.map((order, index) => (
          <OrderPdf order={order} key={index} />
        ))}
      </div>
    </Layout>
  );
};
export default OpenOrderListPage;
